// Accueil d'une collectivité (écran S20) : salut et état de l'identité, « agir au nom de »,
// action attendue mise en avant, demandes en cours, démarches à faire, vérification d'un document.
// Délibérations publiées du conseil (lot L5-B) : lien dans « Documents de la collectivité ».
import { Bouton, EtatVide, LienBouton, Squelette } from '@cc/ui';
import { Check, Paperclip } from 'lucide-react';
import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { useCatalogue, useMesDossiers, useRepresentations } from '../api';
import { codeCollectiviteValide, useCollectiviteChoisie } from '../collectivite';
import { Bloc, Ligne, Liste, Section, TexteStatut } from '../commun';
import { useRepresentationActive } from '../representation';
import { prenomUsager, useSessionUsager } from '../session/FournisseurSessionUsager';
import { actionAttendue, enCours } from '../statuts';
import type { DemarcheCatalogue, ResumeDossierUsager } from '../types';
import styles from '../citoyen.module.css';

function resumeDemarche(d: DemarcheCatalogue): string {
  const pieces = d.pieces.length === 0 ? 'Aucune pièce' : `${d.pieces.length} pièce${d.pieces.length > 1 ? 's' : ''}`;
  return d.delais.reglementaire ? `${pieces} · ${d.delais.reglementaire.libelle}` : pieces;
}

export function coutCourt(d: DemarcheCatalogue): string {
  if (d.tarif.montant === null) return 'Gratuit';
  return `${d.tarif.montant.toLocaleString('fr-FR')} F${d.tarif.mention ? ' (fictif)' : ''}`;
}

function Salut({ code }: { code: string }) {
  const { etat, profil, chargementProfil } = useSessionUsager();
  const representations = useRepresentations();
  const [active, choisir] = useRepresentationActive(representations.data);
  if (etat === 'initialisation' || chargementProfil) return <Squelette lignes={2} libelle="Ouverture de la session" />;
  if (etat !== 'connecte' || !profil) {
    return (
      <section className={styles.pile} aria-labelledby="accueil-titre">
        <h1 id="accueil-titre" className={styles.titrePage}>
          Bienvenue
        </h1>
        <p className={styles.sousTitre}>Déposez vos demandes, suivez-les et prenez rendez-vous avec les services de votre collectivité.</p>
        <div className={styles.ligneBoutons}>
          <LienBouton href="/citoyen/connexion" variante="principal">
            Se connecter
          </LienBouton>
          <LienBouton href="/citoyen/inscription" variante="secondaire">
            Créer un compte
          </LienBouton>
          <Link to={`/citoyen/${code}/demarches`} className={styles.lienSection}>
            Voir les démarches sans compte
          </Link>
        </div>
      </section>
    );
  }
  const valides = (representations.data ?? []).filter((r) => r.valide && r.collectivite.code === code);
  return (
    <section className={styles.pile} aria-labelledby="accueil-titre">
      <div>
        <h1 id="accueil-titre" className={styles.titrePage}>
          Bonjour {prenomUsager(profil)}
        </h1>
        {profil.identite_verifiee ? (
          <p className={styles.statutOk}>
            <Check aria-hidden strokeWidth={2.2} width={14} height={14} /> Identité vérifiée au guichet
          </p>
        ) : (
          <p className={styles.aide}>
            Identité déclarée, non vérifiée. <Link to="/citoyen/compte/identite">Ce que change la vérification</Link>
          </p>
        )}
      </div>
      {valides.length > 0 ? (
        <div role="group" aria-label="Agir au nom de" className={styles.bascule}>
          <button type="button" aria-pressed={active === null} onClick={() => choisir(null)}>
            Moi
          </button>
          {valides.map((r) => (
            <button key={r.id} type="button" aria-pressed={active?.id === r.id} onClick={() => choisir(r.id)}>
              {r.entite_nom}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ActionAttendue({ dossiers }: { dossiers: ResumeDossierUsager[] }) {
  const navigate = useNavigate();
  const d = dossiers.find((x) => actionAttendue(x));
  if (!d) return null;
  const complement = d.statut.code === 'incomplet';
  const cible = complement ? `/citoyen/mes-dossiers/${d.id}/complement` : `/citoyen/${d.collectivite.code}/demarches/${d.demarche.code}/deposer?dossier=${d.id}`;
  return (
    <Bloc etiquette="Une action vous attend" titre={complement ? `Complément demandé sur votre demande « ${d.demarche.libelle} »` : `Brouillon à terminer : ${d.demarche.libelle}`}>
      {d.numero ? <span className={`${styles.mono} ${styles.aide}`}>{d.numero}</span> : null}
      <p className={styles.blocTexte}>
        {complement
          ? d.statut.texte ?? 'Le service a besoin d\'une pièce ou d\'une précision. Sans réponse dans le délai indiqué, la demande sera classée sans suite.'
          : 'Votre demande n\'est pas encore déposée. Reprenez le formulaire où vous l\'aviez laissé.'}
      </p>
      <Bouton pleineLargeur icone={complement ? Paperclip : undefined} onClick={() => navigate(cible)}>
        {complement ? 'Fournir le complément' : 'Reprendre la demande'}
      </Bouton>
    </Bloc>
  );
}

function MesDemandes() {
  const { etat } = useSessionUsager();
  const dossiers = useMesDossiers();
  if (etat !== 'connecte') return null;
  const encours = (dossiers.data ?? []).filter(enCours);
  return (
    <>
      {dossiers.data ? <ActionAttendue dossiers={dossiers.data} /> : null}
      <Section titre="Mes demandes en cours" lien={{ href: '/citoyen/mes-dossiers', libelle: 'Tout voir' }}>
        <EtatRequete chargement={dossiers.isPending} erreur={dossiers.error} onReessayer={() => void dossiers.refetch()} fonction="Vos demandes" lignes={2}>
          {encours.length === 0 ? (
            <p className={styles.aide}>Aucune demande en cours.</p>
          ) : (
            <Liste libelle="Demandes en cours">
              {encours.slice(0, 3).map((d) => (
                <Ligne
                  key={d.id}
                  href={`/citoyen/mes-dossiers/${d.id}`}
                  titre={d.demarche.libelle}
                  sousTitre={
                    <>
                      {d.numero ? <span className={styles.mono}>{d.numero}</span> : 'Brouillon'}
                      {d.representation ? ` · ${d.representation.entite_nom}` : ''}
                    </>
                  }
                  droite={<TexteStatut code={d.statut.code} libelle={actionAttendue(d) ?? undefined} />}
                />
              ))}
            </Liste>
          )}
        </EtatRequete>
      </Section>
    </>
  );
}

export function PageAccueilCollectivite() {
  const { code: brut } = useParams();
  const code = codeCollectiviteValide(brut);
  const catalogue = useCatalogue(code);
  const [choisie, choisir] = useCollectiviteChoisie();
  useEffect(() => {
    document.title = `${catalogue.data?.collectivite.nom ?? 'Accueil'} - Portail citoyen - CollectivitésConnect`;
  }, [catalogue.data]);
  useEffect(() => {
    const c = catalogue.data?.collectivite;
    if (c && (choisie?.code !== c.code || choisie.nom !== c.nom)) choisir({ code: c.code, nom: c.nom });
  }, [catalogue.data, choisie, choisir]);
  if (!code) return <Navigate to="/citoyen" replace />;
  return (
    <>
      <Salut code={code} />
      <MesDemandes />
      <Section titre="Faire une démarche" lien={{ href: `/citoyen/${code}/demarches`, libelle: 'Toutes les démarches' }}>
        <EtatRequete chargement={catalogue.isPending} erreur={catalogue.error} onReessayer={() => void catalogue.refetch()} fonction="Le catalogue des démarches">
          {catalogue.data && catalogue.data.elements.length === 0 ? (
            <EtatVide titre="Aucune démarche en ligne pour l'instant">
              <p>Cette collectivité n&apos;a pas encore publié de démarche sur le portail. Vous pouvez prendre rendez-vous avec ses services.</p>
            </EtatVide>
          ) : (
            <Liste libelle="Démarches">
              {(catalogue.data?.elements ?? []).slice(0, 6).map((d) => (
                <Ligne key={d.code} href={`/citoyen/${code}/demarches/${d.code}`} titre={d.libelle} sousTitre={resumeDemarche(d)} droite={coutCourt(d)} />
              ))}
              <Ligne href="/citoyen/rendez-vous/nouveau" titre="Prendre rendez-vous" sousTitre="Choisir un service et un créneau" droite="Créneaux" />
            </Liste>
          )}
        </EtatRequete>
      </Section>
      <Section titre="Documents de la collectivité">
        <Liste>
          <Ligne href="/citoyen/verification" titre="Vérifier un document par son code" sousTitre="Autorisation, attestation ou décision délivrée par la collectivité" />
          <Ligne href={`/citoyen/${code}/deliberations`} titre="Délibérations du conseil" sousTitre="Textes publiés et suivi de leur exécution" />
          <Ligne href={`/citoyen/${code}/consultations`} titre="Consultations des quartiers" sousTitre="Questions posées aux habitants et résultats" />
          <Ligne href={`/citoyen/${code}/evenements`} titre="Agenda des événements" sousTitre="Événements publiés par la collectivité" />
          <Ligne href={`/citoyen/${code}/urbanisme`} titre="Urbanisme" sousTitre="Démarches d'urbanisme et zones des plans en vigueur" />
        </Liste>
      </Section>
    </>
  );
}
